import React, { useState, useEffect, useRef, forwardRef } from 'react'
import { useDispatch } from 'react-redux'

import { useTheme } from '../../context/theme'
import {
  DMPrimaryButton,
  DMPrimaryDivButton,
  DMPrimaryAltButton,
  DMModal
} from '@decormatters/dm-theme'

import { fetchFeed } from '../../redux/feedInfiniteSlice'
import { setShowMembershipModal } from '../../redux/designerReducers/designerReducer'
import { createMyDecor, removebg } from '../../redux/apis'
import { FEED_RESET } from '../../redux/constants'
import ModalOverlay from '../common/ModalOverlay'

import 'react-image-crop/dist/ReactCrop.css'

import ModalContentContainer from './ModalContentContainer'
import useModal from '../../hooks/useModal'
import styled from 'styled-components'

import { Player } from '@lottiefiles/react-lottie-player'
import circle from '../../assets/throbber.json'
import { ReactComponent as UploadIcon } from '../../assets/icon-upload.svg'
import { logEvent } from '../../utils/firebase'
import locationChangeEvent from '../../utils/locationChangeEvent'
import { useRouter } from 'next/router'

const RemoveBgMyDecorModalContainer = styled(DMModal)`
  background-color: ${props => props.theme.background.secondary};
  background-image: ${props =>
    props.isInitial
      ? `url('${props.theme.modal.remove}')`
      : `url('${props.theme.modal.upload}')`};
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
`

const ContentContainer = styled(ModalContentContainer)`
  display: flex;
  justify-content: center;
  /*
  padding: 0 12px;
  @media only screen and (min-width: 1024px) {
    padding: 0 100px;
  }*/
`

const DropContainer = styled.div`
  margin-bottom: 40px;
  font-weight: bold;
  font-size: 16px;
  text-align: center;
  & img {
    max-width: 340px;
    @media only screen and (min-width: 1024px) {
      max-width: 600px;
    }
  }
`

const DropOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 2;
  color: white;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`

const Loading = styled.div``

const DropArea = styled.div`
  width: 340px;
  height: 300px;
  border-radius: 10px;
  background-color: ${props => props.theme.background.secondary};
  box-shadow: ${props =>
    props.scheme === 'dark'
      ? `0px 0px 10px rgba(255, 255, 255, 0.3)`
      : `0px 0px 10px rgba(255, 94, 109, 0.3)`};
  font-weight: 500;
  font-size: 14px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 26px;

  @media only screen and (min-width: 1024px) {
    width: 600px;
  }
`

const PreviewContainer = styled.div`
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  background-color: white;
  background-image: linear-gradient(45deg, #d4d4d4 25%, transparent 25%),
    linear-gradient(-45deg, #d4d4d4 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #d4d4d4 75%),
    linear-gradient(-45deg, transparent 75%, #d4d4d4 75%);
  background-size: 20px 20px;
  background-position: 0 0, 0 10px, 10px -10px, -10px 0px;

  & img {
    display: block;
  }
`

const SampleContainer = styled.div`
  width: 100%;
  background-color: ${props => props.theme.background.secondary};
  color: #ff5e6d;
  font-weight: bold;
  font-size: 16px;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  padding: 14px;
  gap: 14px;

  & img {
    display: block;
    border-radius: 10px;
    width: 114px;
    height: 114px;
  }
`

const HiddenContainer = styled.div`
  height: 0px;
  width: 0px;
  overflow: hidden;
  opacity: 0;
`

const Doing = styled.div`
  margin-top: 12px;
  color: ${props => props.theme.text.secondary};
  font-weight: bold;
  font-size: 14px;
`

const JoinInfo = styled.div`
  font-weight: normal;
  font-size: 14px;
`

const RemoveBGMyDecorModal = forwardRef(
  (
    {
      show,
      toCrop,
      isMember,
      onClose,
      onComplete,
      onRemoveBGComplete,
      withSaveToMyDecor,
      withOverlay,
      category,
      ...props
    },
    ref
  ) => {
    const SAMPLE =
      'https://decormatters-prod.s3.us-west-1.amazonaws.com/designer/chair_simple_02.jpeg'
    const dispatch = useDispatch()
    const { appearance } = useTheme()
    const rPreview = useRef()
    const rInput = useRef()
    const rForm = useRef()
    const router = useRouter()

    useModal(show)

    const [submitting, setSubmitting] = useState(false)
    const [removeStatus, setRemoveStatus] = useState('none')
    const [dragging, setDragging] = useState(false)
    const [cur, setCur] = useState(null)
    const [removeResult, setRemoveResult] = useState()

    useEffect(() => {
      if (show) {
        setSubmitting(false)
        setRemoveStatus('none')
        setDragging(false)
        setCur(null)
        setRemoveResult(null)
      }
    }, [show])

    function blobToBase64(blob) {
      return new Promise((resolve, _) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(reader.result)
        reader.readAsDataURL(blob)
      })
    }

    const handleDragOver = e => {
      e.preventDefault()
      e.stopPropagation()
      setDragging(true)
    }

    const handleDragLeave = e => {
      e.preventDefault()
      e.stopPropagation()
      setDragging(false)
    }

    const handleDrop = async e => {
      e.preventDefault()
      e.stopPropagation()

      setDragging(false)

      if (!e.dataTransfer.files) return
      if (e.dataTransfer.files.length === 0) return

      const localImageUrl = window.URL.createObjectURL(e.dataTransfer.files[0])
      setCur(localImageUrl)
      setSubmitting(true)
      setRemoveStatus('removing')

      const result = await removebg(e.dataTransfer.files[0])

      setSubmitting(false)
      setRemoveStatus('complete')

      if (!result) return
      if (result.exe) return

      setRemoveResult(result)
      setCur(URL.createObjectURL(result))
      e.dataTransfer.clearData()
    }

    const handleOk = async () => {
      logEvent('my_decor_upload_complete', {
        content_type: 'my_decor'
      })

      setSubmitting(true)

      if (!cur) {
        setSubmitting(false)
        return
      }

      const b64 = await blobToBase64(removeResult)

      const imageData = {
        __type: 'Bytes',
        base64: b64
      }

      const mydecorData = await createMyDecor(imageData, { category })
      dispatch(fetchFeed('mydecor')({ status: FEED_RESET }))
      if (onComplete) onComplete()
      setSubmitting(false)
      setRemoveStatus('none')
      setCur(null)
      setRemoveResult(null)

      if (locationChangeEvent.currentValue === '/') {
        router.push('/create/design/new', {
          idea: {
            id: mydecorData.objectId,
            template:
              'https://cdn.decormatters.com/dm/templates/2io2swrohcjrhd8ymrxrha.jpeg',
            targets: [
              {
                id: mydecorData.objectId,
                m: [375, 375, 1, 1, 0],
                rotation: 0,
                src: mydecorData.imageFile.url,
                f: 1,
                item: {
                  categoryId: null,
                  id: mydecorData.objectId,
                  imgurl: mydecorData.imageFile.url,
                  price: 0,
                  subcategoryId: null,
                  title: 'MyDecor',
                  type: 'u',
                  userId: mydecorData.user.objectId
                }
              }
            ]
          },
          type: 'templates'
        })
      }
    }

    const handleSaveToMyDecor = async () => {
      await handleOk()
      router.push('/mydecor')
    }

    const handleOpenFile = async e => {
      e.preventDefault()
      e.stopPropagation()

      if (!e.target) return
      if (!e.target.files) return
      if (!e.target.files[0]) return

      logEvent('my_decor_upload_browse_file', {
        content_type: 'my_decor'
      })

      if (e.target.files[0].size > 2097152) {
        alert('Please upload images under 2MB')
        return
      }

      const file = e.target.files[0]
      const localImageUrl = window.URL.createObjectURL(file)
      setCur(localImageUrl)
      setSubmitting(true)
      setRemoveStatus('removing')
      rForm.current.reset()
      const result = await removebg(file)

      setSubmitting(false)
      setRemoveStatus('complete')

      if (!result) return
      if (result.exe) return

      setRemoveResult(result)
      setCur(URL.createObjectURL(result))
    }

    const handleToMembership = e => {
      logEvent('show_purchase_coin', {
        content_type: 'purchase'
      })
      if (onClose) onClose()
      dispatch(setShowMembershipModal(true))
    }

    const handleSample = async e => {
      const res = await fetch(SAMPLE + '?1111')
      const d = await res.arrayBuffer()
      const f = new File([d], 'sample.jpeg', { type: 'image/jpeg' })

      setCur(SAMPLE)
      setSubmitting(true)
      setRemoveStatus('removing')

      const result = await removebg(f)

      setSubmitting(false)
      setRemoveStatus('complete')

      if (!result) return
      if (result.exe) return

      setRemoveResult(result)
      setCur(URL.createObjectURL(result))
    }

    const render = () => {
      return (
        <ModalOverlay>
          <RemoveBgMyDecorModalContainer
            isInitial={removeStatus === 'none' ? true : false}
            show={show}
            title=""
            scheme={appearance}
            maxWidth={800}
            noOverlayClose
            onClose={onClose}
            size={DMModal.MODAL_SIZE_FULLSCREEN}
            {...props}
          >
            <ContentContainer draggable="false">
              <DropContainer>
                {!cur && (
                  <DropArea
                    scheme={appearance}
                    dragging={dragging}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <UploadIcon />
                    <form ref={rForm} onSubmit={handleOpenFile}>
                      <HiddenContainer>
                        <input
                          ref={rInput}
                          type="file"
                          id="rembg"
                          name="rembg"
                          accept="image/png,image/jpeg"
                          onChange={e => {
                            handleOpenFile(e)
                          }}
                        />
                      </HiddenContainer>
                      <label htmlFor="rembg">
                        <DMPrimaryDivButton>Upload Image</DMPrimaryDivButton>
                      </label>
                    </form>
                    Or drag & drop an image
                  </DropArea>
                )}
                {removeStatus === 'complete' && isMember && (
                  <div style={{ marginBottom: '30px' }}>
                    Your item is ready to use.
                  </div>
                )}
                {removeStatus === 'complete' && !isMember && (
                  <>
                    <JoinInfo>Your item is ready.</JoinInfo>
                    <JoinInfo style={{ marginBottom: '30px' }}>
                      Join our membership for{' '}
                      <b>unlimited usage and designing.</b>
                    </JoinInfo>
                  </>
                )}
                {cur && (
                  <>
                    <PreviewContainer>
                      <img ref={rPreview} src={cur} alt="Preview" />
                      {submitting && (
                        <DropOverlay>
                          <Loading>
                            <Player
                              loop
                              autoplay={true}
                              src={circle}
                              style={{
                                height: '300px',
                                width: '300px'
                              }}
                            />
                          </Loading>
                        </DropOverlay>
                      )}
                    </PreviewContainer>
                    {submitting && <Doing>Doing the magic...</Doing>}
                  </>
                )}
                {removeStatus === 'complete' && isMember && (
                  <>
                    {withSaveToMyDecor && (
                      <DMPrimaryAltButton
                        style={{ margin: '30px 30px 0 0' }}
                        scheme={appearance}
                        type="button"
                        onClick={handleSaveToMyDecor}
                        disabled={submitting}
                      >
                        Save to MyDecor
                      </DMPrimaryAltButton>
                    )}
                    <DMPrimaryButton
                      style={{ marginTop: '30px' }}
                      scheme={appearance}
                      type="button"
                      onClick={handleOk}
                      disabled={submitting}
                    >
                      Use it Now
                    </DMPrimaryButton>
                  </>
                )}
                {removeStatus === 'complete' && !isMember && (
                  <DMPrimaryButton
                    style={{ marginTop: '30px' }}
                    scheme={appearance}
                    type="button"
                    onClick={handleToMembership}
                    disabled={submitting}
                  >
                    Join Membership
                  </DMPrimaryButton>
                )}
              </DropContainer>
              {removeStatus === 'none' && (
                <SampleContainer>
                  <div>or select sample image:</div>
                  <img src={SAMPLE} alt="Sample Remove BG" />
                  {removeStatus === 'none' && (
                    <>
                      <DMPrimaryAltButton
                        scheme={appearance}
                        type="button"
                        onClick={handleSample}
                        disabled={submitting}
                      >
                        Select
                      </DMPrimaryAltButton>
                    </>
                  )}
                </SampleContainer>
              )}
            </ContentContainer>
          </RemoveBgMyDecorModalContainer>
        </ModalOverlay>
      )
    }

    if (!show) return null

    return withOverlay ? <ModalOverlay>{render()}</ModalOverlay> : render()
  }
)

export default RemoveBGMyDecorModal
